import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGenericWorkflowsByCustomer } from "./data/useGenericWorkflowsByCustomer";
import { useWorkflowsBySubstationAndCustomer } from "./data/useWorkflowsBySubstationAndCustomer";

import { Box, CircularProgress, Typography } from "@mui/material";
import { Dropdown, DropdownProps } from "primereact/dropdown";

import { FieldValues, FormState, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { MessageToast, ToastMode } from "./shared/MessageToast";

import { getAssetTypeLabel, managedSurveyAssetType } from "./shared/constants/managedSurveyAssetTypes";
import { Asset } from "./shared/interfaces/Asset";
import { WorkflowV1 } from "./shared/interfaces/WorkflowV1";
import { WorkflowV2 } from "./shared/interfaces/WorkflowV2";
import { ValidationMessage } from "./shared/ValidationMessage";

interface Props {
  customerId?: number;
  substationId?: number;
  onWorkflowChange: (workflow: WorkflowV1 | WorkflowV2) => void;
  selectedWorkflowId?: number;
  selectedAssets?: Asset[];
  register?: UseFormRegister<FieldValues>;
  formState?: FormState<FieldValues>;
  setValue?: UseFormSetValue<FieldValues>;
  initialiseFormState?: boolean;
  invalid?: boolean;
  invalidMessage?: string;
  width?: string;
}

interface WorkflowGroup {
  group: string;
  workflows: (WorkflowV1 | WorkflowV2)[];
}

export function WorkflowDropdown(props: Props & DropdownProps) {
  const {
    customerId,
    substationId,
    selectedWorkflowId,
    onWorkflowChange,
    selectedAssets,
    register,
    formState,
    setValue,
    initialiseFormState,
    ...rest
  } = props;

  const { t } = useTranslation();

  const selectedAssetTypes = selectedAssets?.map((asset) => getAssetTypeLabel(asset.assetTypeKey)) ?? [];

  const generics = useGenericWorkflowsByCustomer(customerId);

  const genericCustomerWorkflows = generics.data?.filter((workflow) => {
    let workflowAssetTypes: string[] = [];
    if (workflow.workflowDetail.version !== 1)
      workflowAssetTypes = workflow.workflowDetail.assets.map((asset) => asset.asset_type);
    else workflowAssetTypes = [managedSurveyAssetType.Switchgear];
    const allSelectedTypesMatch = selectedAssetTypes.every((type) => workflowAssetTypes.includes(type));
    if (
      selectedAssets?.length !== 1 ||
      selectedAssetTypes.filter((assetType) => assetType !== managedSurveyAssetType.Switchgear).length > 0
    ) {
      return workflow.workflowDetail.version !== 1 && allSelectedTypesMatch;
    } else {
      return allSelectedTypesMatch;
    }
  });

  const substationSpecifics = useWorkflowsBySubstationAndCustomer(substationId, customerId);

  const substationSpecificWorkflows = substationSpecifics.data?.filter((workflow) => {
    if (workflow.workflowDetail.version !== 1) {
      const workflowAssetIds = workflow.workflowDetail.assets.map((asset) => asset.assetid);
      return selectedAssets?.every((asset) => workflowAssetIds.includes(asset.id)) ?? false;
    } else {
      return workflow.workflowDetail.switchgear_id === selectedAssets?.[0]?.id;
    }
  });

  const switchgearSpecificWorkflows = { isLoading: undefined, data: undefined, isError: undefined }; //useWorkflowsPreviouslyUsedOnAsset(selectedAssetIds); TODO: The Endpoint needs updating to support this.

  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowV1 | WorkflowV2 | undefined>(undefined);
  const groupedDropdownData: WorkflowGroup[] = useMemo(() => {
    if (generics.loading || substationSpecifics.loading) {
      return [];
    }

    return [
      {
        group: t("workflows.substationSpecific"),
        workflows: substationSpecificWorkflows ?? [],
      },
      {
        group: t("workflows.generic"),
        workflows: genericCustomerWorkflows ?? [],
      },
    ];
  }, [generics.loading, substationSpecifics.loading, substationSpecificWorkflows, genericCustomerWorkflows, t]);

  const hasSetInitialWorkflow = useRef(false);

  useEffect(() => {
    if (!initialiseFormState || !setValue || !substationId || !selectedWorkflowId || hasSetInitialWorkflow.current) {
      return;
    }

    const workflow = groupedDropdownData
      .flatMap((workflowGroup) => workflowGroup.workflows)
      .find((workflow) => workflow.id === selectedWorkflowId);

    if (workflow) {
      setValue("workflow", workflow);
      setSelectedWorkflow(workflow);
      hasSetInitialWorkflow.current = true;
    } else {
      setValue("workflow", undefined);
      setSelectedWorkflow(undefined);
    }
  }, [groupedDropdownData, selectedWorkflowId, initialiseFormState, setValue, substationId]);

  function handleChange(e: any) {
    setValue && setValue(`workflow`, e.value, { shouldValidate: true });
    setSelectedWorkflow(e.value);
    onWorkflowChange(e.value);
  }

  useEffect(() => {
    if (!selectedWorkflowId) {
      setSelectedWorkflow(undefined);
    } else if (
      selectedWorkflowId &&
      !generics.loading &&
      !substationSpecifics.loading &&
      !switchgearSpecificWorkflows.isLoading &&
      !generics.error &&
      !substationSpecifics.error &&
      !switchgearSpecificWorkflows.isError
    ) {
      for (const group of groupedDropdownData) {
        const workflow = group.workflows.find((workflow) => workflow.id === selectedWorkflowId);
        workflow && setSelectedWorkflow(workflow);
      }
    }
  }, [
    genericCustomerWorkflows,
    generics.error,
    generics.loading,
    groupedDropdownData,
    selectedWorkflowId,
    substationSpecificWorkflows,
    substationSpecifics.error,
    substationSpecifics.loading,
    switchgearSpecificWorkflows.data,
    switchgearSpecificWorkflows.isError,
    switchgearSpecificWorkflows.isLoading,
    t,
  ]);

  const optionLabelTemplate = (option: WorkflowV1, props: DropdownProps) => {
    if (option)
      return (
        <Box display="flex" flexDirection="row" alignItems="baseline">
          {option.description}
          <Typography variant="subtitle2" className="ml-2">{`(${t("common.id", { val: option.id })})`}</Typography>
        </Box>
      );
    else return props.placeholder;
  };

  if (generics.loading || substationSpecifics.loading || switchgearSpecificWorkflows.isLoading) {
    return <CircularProgress size={24} />;
  } else if (generics.error || substationSpecifics.error || switchgearSpecificWorkflows.isError) {
    return (
      <MessageToast text={t("common.dataFetchError", { val: "$t(workflows.workflow)" })} mode={ToastMode.Error} show />
    );
  } else if (register) {
    return (
      <div>
        <Dropdown
          className={formState && formState.errors["workflow"] ? "p-invalid" : undefined}
          disabled={!substationId || !customerId}
          value={selectedWorkflow}
          options={groupedDropdownData}
          filter
          filterBy="description"
          optionGroupChildren="workflows"
          optionLabel="description"
          itemTemplate={optionLabelTemplate}
          valueTemplate={optionLabelTemplate}
          optionGroupLabel="group"
          placeholder={t("common.select", { item: "$t(workflows.workflow)" })}
          {...register(`workflow`, { required: true })}
          onChange={handleChange}
          style={{ width: props.width || 200 }}
          {...rest}
        />

        {formState && formState.errors["workflow"] && (
          <ValidationMessage
            invalidMessage={t("common.pleaseEnter", {
              val: "$t(workflows.workflow)",
            })}
            validationType="error"
            showIcon
          />
        )}
      </div>
    );
  } else
    return (
      <>
        <Dropdown
          disabled={!substationId || !customerId}
          value={selectedWorkflow}
          options={groupedDropdownData}
          onChange={handleChange}
          filter
          filterBy="description"
          optionGroupChildren="workflows"
          optionLabel="description"
          itemTemplate={optionLabelTemplate}
          valueTemplate={optionLabelTemplate}
          optionGroupLabel="group"
          placeholder={t("common.select", { item: "$t(workflows.workflow)" })}
          className={props.invalid ? "p-invalid" : undefined}
          style={{ width: props.width || 200 }}
          {...rest}
        />
        {props.invalid && props.invalidMessage && (
          <ValidationMessage invalidMessage={props.invalidMessage} validationType="error" showIcon />
        )}
      </>
    );
}
